// Background-audio playback for the webplayer. Mirrors Android's
// com.wetook.app.audio.AudioController — both consume the same `audio` +
// `audio_loop_all` fields from /devices/content/{token}.
//
// Rules:
//   • audio_loop_all ON  → play each track once in order, then repeat the
//     whole sequence from the top. Overrides per-track `loop`.
//   • audio_loop_all OFF → each track honors its own `loop`:
//       loop=true  → repeats infinitely (uses native HTMLAudioElement.loop,
//                    so the `ended` event never fires; playback stays on that
//                    track until the operator changes the setting)
//       loop=false → plays once, then advances; stops at end of sequence
//
// `audio_unmuted` (screen-level) gates all audio output. The hook also pauses
// while `enabled` (the App's "showingContent" flag) is false so loaders and
// state screens don't have music playing underneath.

import { useEffect, useRef } from 'react'
import { resolveUrl, type ContentResponse } from '../lib/deviceApi'

export function useBackgroundAudio(
  content: ContentResponse | null,
  enabled: boolean,
  unlocked: boolean = false,
): void {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const idxRef = useRef(0)
  // Identity of the currently-applied set; if a re-render produces the same
  // key the effect skips work so playback isn't restarted on every render.
  const appliedKeyRef = useRef('')

  const tracks = content?.audio ?? []
  const sequenceLoop = !!content?.audio_loop_all
  const audioUnmuted = !!content?.audio_unmuted
  // Screen master level 0–100 scales every track's own volume.
  const masterVolume = Math.max(0, Math.min(100, content?.volume ?? 100))
  const setKey =
    tracks.map(t => `${t.id}:${t.volume}:${t.loop}`).join('|') +
    `|seq=${sequenceLoop}|en=${enabled}|mute=${!audioUnmuted}|unlocked=${unlocked}|vol=${masterVolume}`

  // Lazy-init the element once, and tear it down on unmount.
  useEffect(() => {
    if (!audioRef.current && typeof Audio !== 'undefined') {
      audioRef.current = new Audio()
    }
    return () => {
      const a = audioRef.current
      if (a) {
        try { a.pause(); a.src = '' } catch { /* ignore */ }
        audioRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (setKey === appliedKeyRef.current) return
    const prevKey = appliedKeyRef.current
    appliedKeyRef.current = setKey

    // The track-list identity portion of the key — used to decide whether
    // to reset to index 0 vs preserve the current position (volume/loop
    // changes on the same list shouldn't restart playback).
    const trackListKey = (key: string) => key.split('|seq=')[0]
    if (trackListKey(setKey) !== trackListKey(prevKey)) {
      idxRef.current = 0
    }

    const a = audioRef.current
    if (!a) return

    const playable = enabled && audioUnmuted && tracks.length > 0 && unlocked
    if (!playable) {
      try { a.pause() } catch { /* ignore */ }
      a.onended = null
      return
    }

    const playCurrent = () => {
      const t = tracks[idxRef.current % tracks.length]
      if (!t) return
      const url = resolveUrl(t.url) ?? ''
      // Only reassign src when it actually changes — avoids a network
      // restart when only volume or loop were tweaked.
      if (a.src !== url) a.src = url
      // Per-track loop is only honored when sequence-loop is off; sequence
      // mode needs the `ended` event to advance, which native loop=true
      // would prevent.
      a.loop = !sequenceLoop && !!t.loop
      // Track volume scaled by the screen's master level (both 0–100).
      a.volume = (Math.max(0, Math.min(100, t.volume ?? 100)) / 100) * (masterVolume / 100)
      void a.play().catch(() => { /* autoplay blocked — ignore */ })
    }

    a.onended = () => {
      if (sequenceLoop) {
        idxRef.current = (idxRef.current + 1) % tracks.length
        playCurrent()
      } else {
        // loop=true keeps the element looping natively, so this branch only
        // runs when the current track's loop=false. Advance once; stop at
        // the end of the list (no wraparound) since sequence-loop is off.
        const next = idxRef.current + 1
        if (next < tracks.length) {
          idxRef.current = next
          playCurrent()
        }
      }
    }

    playCurrent()
  }, [setKey, tracks, sequenceLoop, enabled, audioUnmuted, unlocked, masterVolume])
}
